// ============================================
// PAGE - Tutoriales
// ============================================

import { useState } from 'react';
import { MdArrowBack, MdVideoLibrary } from 'react-icons/md';
import { useModulosTutoriales, useTutorialModulo } from '@almacenes/core';
import { Spinner } from '@almacenes/ui';

export const TutorialesPage = () => {
  const [moduloSeleccionado, setModuloSeleccionado] = useState<string | null>(null);
  const [stepActual, setStepActual] = useState(0);

  const { data: modulos, isLoading: isLoadingModulos } = useModulosTutoriales();
  const { data: tutorial, isLoading: isLoadingTutorial } = useTutorialModulo(
    moduloSeleccionado || '',
    !!moduloSeleccionado
  );

  const handleSelectModulo = (moduleName: string) => {
    setModuloSeleccionado(moduleName);
    setStepActual(0);
  };

  const handleBackToModulos = () => {
    setModuloSeleccionado(null);
    setStepActual(0);
  };

  const handleNextStep = () => {
    if (tutorial && stepActual < tutorial.steps.length - 1) {
      setStepActual(stepActual + 1);
    }
  };

  const handlePrevStep = () => {
    if (stepActual > 0) {
      setStepActual(stepActual - 1);
    }
  };

  const currentStep = tutorial?.steps[stepActual];

  // Vista de selección de módulos
  if (!moduloSeleccionado) {
    return (
      <div className="px-4 md:px-6 lg:px-8 pb-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
              Tutoriales
            </h1>
            <p className="text-gray-600 mt-1">
              Selecciona un módulo para ver los tutoriales disponibles
            </p>
          </div>
        </div>

        {/* Grid de módulos */}
        {isLoadingModulos ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {modulos?.map((modulo) => (
              <button
                key={modulo.moduleName}
                onClick={() => handleSelectModulo(modulo.moduleName)}
                className="group bg-white border border-gray-200 rounded-lg p-5 hover:border-blue-400 hover:shadow-md transition-all duration-200 text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="text-3xl flex-shrink-0">{getModuleIcon(modulo.moduleName)}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base group-hover:text-blue-600 transition-colors truncate" style={{ color: '#11194F' }}>
                      {modulo.moduleName}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">Tutorial interactivo</p>
                  </div>
                  <MdVideoLibrary className="text-xl text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vista de tutorial
  if (isLoadingTutorial) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (!tutorial || !currentStep) {
    return (
      <div className="px-4 md:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center max-w-md mx-auto">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#11194F' }}>
            No se encontró el tutorial
          </h2>
          <p className="text-gray-600 mb-6">
            No pudimos cargar la información de este tutorial
          </p>
          <button
            onClick={handleBackToModulos}
            className="px-6 py-3 rounded-lg font-medium text-white"
            style={{ backgroundColor: '#43BFFF' }}
          >
            Volver a módulos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={handleBackToModulos}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <MdArrowBack className="text-2xl" style={{ color: '#43BFFF' }} />
        </button>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
            Tutorial: {tutorial.moduleName}
          </h1>
          <p className="text-gray-600 mt-1">
            {tutorial.moduleDescription}
          </p>
        </div>
      </div>

      {/* Contenido del tutorial */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Lista de pasos */}
        <aside className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-4">
            <h3 className="font-bold text-lg mb-4" style={{ color: '#11194F' }}>
              Pasos del tutorial
            </h3>
            
            <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
              {tutorial.steps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => setStepActual(index)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    index === stepActual
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                        index === stepActual
                          ? 'bg-white text-blue-500'
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold text-sm ${
                        index === stepActual ? 'text-white' : 'text-gray-900'
                      }`}>
                        {step.title}
                      </p>
                      <p className={`text-xs mt-1 line-clamp-2 ${
                        index === stepActual ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Navegación */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex gap-2 mb-2">
                <button
                  onClick={handlePrevStep}
                  disabled={stepActual === 0}
                  className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                    stepActual === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  ← Anterior
                </button>
                <button
                  onClick={handleNextStep}
                  disabled={stepActual === tutorial.steps.length - 1}
                  className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                    stepActual === tutorial.steps.length - 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'text-white hover:opacity-90'
                  }`}
                  style={{
                    backgroundColor: stepActual === tutorial.steps.length - 1 ? undefined : '#43BFFF'
                  }}
                >
                  Siguiente →
                </button>
              </div>
              <p className="text-center text-sm text-gray-500">
                Paso {stepActual + 1} de {tutorial.steps.length}
              </p>
            </div>
          </div>
        </aside>

        {/* Panel principal - Video */}
        <main className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Título del paso actual */}
            <div className="mb-4">
              <h2 className="font-bold text-2xl mb-2" style={{ color: '#11194F' }}>
                {currentStep.title}
              </h2>
            </div>

            {/* Video embebido */}
            <div className="relative w-full rounded-lg overflow-hidden shadow-lg bg-gray-900" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={currentStep.videoLink}
                className="absolute top-0 left-0 w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={currentStep.title}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Helper para obtener iconos según el módulo
function getModuleIcon(moduleName: string): string {
  const icons: Record<string, string> = {
    'Inventario': '📦',
    'Venta': '🛒',
    'Reporte': '📊',
    'Fiados': '💰',
    'Detalle Boleta': '🧾',
    'Configurar': '⚙️',
    'Ecommerce': '🏪',
    'Resumen SII': '📄',
  };
  return icons[moduleName] || '📚';
}
